import { useTranslation } from 'react-i18next';
import { ConfidenceIndicator } from '@/components/trust/ConfidenceIndicator';
import { formatEngineVersion } from '@/lib/engineVersion';

/** Dominant primary result: the number a CFO reads first. */
export function EstimateHeadline({ results, config, formatCost, name, legacy = false }) {
  const { t, i18n } = useTranslation();

  const sizeLabel = config.companySize ? t(`calcData.sizes.${config.companySize}`) : '';
  const industryLabel = config.industry ? t(`calcData.industries.${config.industry}`) : '';
  const calculatedAt = results.calculatedAt || results.methodology?.calculatedAt;
  const dateLabel = new Date(calculatedAt || Date.now()).toLocaleDateString(i18n.language, {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <section
      className="border border-[#DDE1E5] rounded-lg bg-white p-6 sm:p-7"
      data-testid="estimate-headline"
      aria-label={t('results.title')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#66707A]"
               style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            {t('results.title')}
          </div>
          <h2 className="mt-1 text-base md:text-lg font-semibold text-[#171A1D]" data-testid="estimate-subject">
            {name || `${sizeLabel} · ${industryLabel}`}
          </h2>
          <p className="mt-0.5 text-xs text-[#8A929B]" data-testid="estimate-calculated-at">
            {t('trust.calculatedAt')} {dateLabel}
            {results.engineVersion ? ` · ${formatEngineVersion(results.engineVersion)}` : ''}
          </p>
        </div>
        {legacy && (
          <span
            className="inline-flex items-center border border-[#DDE1E5] bg-[#F5F6F7] text-[#66707A] px-2 py-1 rounded-sm text-[10px] uppercase tracking-[0.08em]"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            title={t('trust.legacyHint')}
            data-testid="legacy-estimate-badge"
          >
            {t('trust.legacyBadge')}
          </span>
        )}
      </div>

      <div className="mt-6">
        <div
          className="text-4xl font-semibold text-[#171A1D] break-words"
          style={{ fontFamily: 'IBM Plex Mono, monospace', fontVariantNumeric: 'tabular-nums' }}
          data-testid="estimate-total-cost"
        >
          {formatCost(results.totalCost)}
        </div>
        <p className="mt-1.5 text-sm text-[#66707A]">{t('results.costRange')}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span
            className="text-sm text-[#3A4249]"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            data-testid="estimate-planning-range"
          >
            {formatCost(results.costRange.low)} – {formatCost(results.costRange.high)}
          </span>
          <span className="text-xs text-[#8A929B]">{t('trust.planningRange')}</span>
          <ConfidenceIndicator confidence={results.confidence} compact />
        </div>
      </div>
    </section>
  );
}
